import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Download, Wifi, Camera, X } from 'lucide-react';

interface AgentConfigOverlayProps {
  onClose: () => void;
  onNavigateToAgents: () => void;
}

export function AgentConfigOverlay({ onClose, onNavigateToAgents }: AgentConfigOverlayProps) {
  const [pairCode, setPairCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const generatePairCode = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('csense_auth_token');
      const response = await fetch('/api/agent/generate-pair-code', {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPairCode(data.pair_code);
      }
    } catch (error) {
      console.error('Failed to generate pair code:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="bg-gray-900 border-gray-800 max-w-2xl w-full">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Camera className="h-6 w-6 text-blue-500" />
              Configure Your Agent
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Wifi className="h-8 w-8 text-blue-500" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              No Agents Connected
            </h3>
            <p className="text-gray-400 mb-6">
              To start monitoring with C.S.E.N.S.E, you need to configure an agent that will connect your cameras.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
            <div className="p-4 border border-gray-700 rounded-lg">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-2">1</div>
              <h4 className="text-white font-medium mb-1">Download Agent</h4>
              <p className="text-gray-400 text-sm">Download the C.SENSE agent for your system</p>
            </div>
            
            <div className="p-4 border border-gray-700 rounded-lg">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-2">2</div>
              <h4 className="text-white font-medium mb-1">Generate Code</h4>
              <p className="text-gray-400 text-sm">Create a secure pairing code</p>
            </div>
            
            <div className="p-4 border border-gray-700 rounded-lg">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold mx-auto mb-2">3</div>
              <h4 className="text-white font-medium mb-1">Connect</h4>
              <p className="text-gray-400 text-sm">Agent discovers your cameras automatically</p>
            </div>
          </div>

          <div className="space-y-3">
            <Button
              onClick={generatePairCode}
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              {loading ? 'Generating...' : 'Generate Pair Code'}
            </Button>
            
            <a
              href="https://github.com/your-repo/releases/latest/download/csense-agent-windows.exe"
              className="w-full bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors inline-flex items-center justify-center"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download className="w-4 h-4 mr-2" />
              Download Windows Agent
            </a>
          </div>

          {pairCode && (
            <div className="p-4 bg-green-900/20 border border-green-500 rounded-lg text-center">
              <p className="text-green-400 font-medium mb-2">Pair Code Generated</p>
              <p className="text-2xl font-mono text-white mb-2">{pairCode}</p>
              <p className="text-gray-400 text-sm">
                Use this code in your agent to connect your cameras
              </p>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onNavigateToAgents}
              className="w-full border-gray-700 text-gray-300"
            >
              Go to Agents Tab
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}